/**
 * ChessAI - Minimax AI with Alpha-Beta Pruning
 * Built-in chess AI for ChessRogue with obstacle/wall awareness
 * Supports variable board sizes (5x5-8x8) and difficulty scaling
 */

import { CustomChessEngine, Piece } from './CustomChessEngine';
import { PieceType, Square, AIConfig } from '../../types';

export interface ScoredMove {
  from: Square;
  to: Square;
  score: number;
  promotion?: PieceType;
}

interface SearchState {
  engine: CustomChessEngine;
  obstacles: Set<string>;
  waterSquares: Set<string>;
  walls: Map<string, Set<string>>;
  boardCenter: { file: number; rank: number };
  minRank: number; // bottom rank of the (possibly restricted) board
  maxRank: number; // top rank of the board
}

interface SearchResult {
  bestMove: ScoredMove | null;
  score: number;
  depth: number;
  nodesSearched: number;
  allMoves: ScoredMove[];
}

// Material values in centipawns
const PIECE_VALUES: Record<PieceType, number> = {
  p: 100,
  n: 320,
  b: 330,
  r: 500,
  q: 900,
  k: 20000,
};

const INF = 999999;

export class ChessAI {
  private aiConfig: AIConfig;
  private nodesSearched = 0;
  private searchStartTime = 0;
  private timeLimit = 0;

  constructor(aiConfig: AIConfig) {
    this.aiConfig = aiConfig;
  }

  /**
   * Entry point: find the best move for the current position
   */
  getBestMove(
    engine: CustomChessEngine,
    obstacles: Set<string>,
    waterSquares: Set<string>,
    walls: Map<string, Set<string>>,
    validSquares: Set<string>
  ): ScoredMove | null {
    this.nodesSearched = 0;
    this.searchStartTime = Date.now();
    this.timeLimit = this.aiConfig.moveTime || 5000;

    const boardCenter = this.computeBoardCenter(validSquares);
    const { minRank, maxRank } = this.computeRankBounds(validSquares);

    const state: SearchState = {
      engine: engine.clone(),
      obstacles,
      waterSquares,
      walls,
      boardCenter,
      minRank,
      maxRank,
    };

    const moves = this.generateMoves(state);
    if (moves.length === 0) return null;
    if (moves.length === 1) return moves[0];

    const maxDepth = this.aiConfig.depth;
    const result = this.iterativeDeepening(state, maxDepth, moves);

    console.log(
      `ChessAI: depth=${result.depth}, nodes=${result.nodesSearched}, ` +
      `time=${Date.now() - this.searchStartTime}ms, ` +
      `score=${result.score}`
    );

    return this.applyDifficultyRandomness(result.allMoves) || result.bestMove;
  }

  /**
   * Iterative deepening: search at increasing depths, keeping best result
   */
  private iterativeDeepening(
    state: SearchState,
    maxDepth: number,
    rootMoves: ScoredMove[]
  ): SearchResult {
    let bestResult: SearchResult = {
      bestMove: rootMoves[0],
      score: 0,
      depth: 0,
      nodesSearched: 0,
      allMoves: rootMoves,
    };

    // Use a mutable copy for reordering across iterations
    let orderedMoves = rootMoves.slice();

    for (let depth = 1; depth <= maxDepth; depth++) {
      if (Date.now() - this.searchStartTime > this.timeLimit * 0.8) break;

      const scoredMoves: ScoredMove[] = [];
      let alpha = -INF;
      const beta = INF;

      for (const move of orderedMoves) {
        const childState = this.makeSearchMove(state, move);
        if (!childState) continue;

        const score = -this.negamax(childState, depth - 1, -beta, -alpha);
        scoredMoves.push({ ...move, score });

        if (score > alpha) {
          alpha = score;
        }

        if (Date.now() - this.searchStartTime > this.timeLimit * 0.8) break;
      }

      if (scoredMoves.length === 0) break;

      // Sort by score descending for next iteration (best-first)
      scoredMoves.sort((a, b) => b.score - a.score);
      orderedMoves = scoredMoves;

      bestResult = {
        bestMove: scoredMoves[0],
        score: scoredMoves[0].score,
        depth,
        nodesSearched: this.nodesSearched,
        allMoves: scoredMoves,
      };

      // Stop early if checkmate found
      if (Math.abs(bestResult.score) > PIECE_VALUES.k - 1000) break;
    }

    return bestResult;
  }

  /**
   * Negamax with alpha-beta pruning
   * Score is always from perspective of side to move
   */
  private negamax(state: SearchState, depth: number, alpha: number, beta: number): number {
    this.nodesSearched++;

    // Time check every 1024 nodes
    if ((this.nodesSearched & 1023) === 0) {
      if (Date.now() - this.searchStartTime > this.timeLimit) {
        return this.evaluate(state);
      }
    }

    const isInCheck = state.engine.isCheck();

    // Check extension: search one more ply when in check
    if (isInCheck && depth <= 0) {
      depth = 1;
    }

    if (depth <= 0) {
      if (this.aiConfig.difficulty === 'expert') {
        return this.quiescenceSearch(state, alpha, beta, 4);
      }
      return this.evaluate(state);
    }

    const moves = this.generateMoves(state);

    if (moves.length === 0) {
      if (isInCheck) {
        // Checkmate — prefer faster mates (higher penalty at shallower depth)
        return -(PIECE_VALUES.k - (this.aiConfig.depth - depth));
      }
      return 0; // Stalemate
    }

    this.orderMoves(moves, state);

    for (const move of moves) {
      const childState = this.makeSearchMove(state, move);
      if (!childState) continue;

      const score = -this.negamax(childState, depth - 1, -beta, -alpha);

      if (score >= beta) {
        return beta; // Beta cutoff
      }
      if (score > alpha) {
        alpha = score;
      }
    }

    return alpha;
  }

  /**
   * Quiescence search: only examine captures to resolve tactical positions
   * Used at expert difficulty to avoid horizon effect
   */
  private quiescenceSearch(
    state: SearchState,
    alpha: number,
    beta: number,
    maxDepth: number
  ): number {
    this.nodesSearched++;

    const standPat = this.evaluate(state);
    if (maxDepth <= 0) return standPat;
    if (standPat >= beta) return beta;
    if (standPat > alpha) alpha = standPat;

    // Only search captures
    const allMoves = this.generateMoves(state);
    const captures = allMoves.filter(m => {
      const target = state.engine.getPieceAt(m.to);
      return target !== null;
    });

    this.orderMoves(captures, state);

    for (const move of captures) {
      const childState = this.makeSearchMove(state, move);
      if (!childState) continue;

      const score = -this.quiescenceSearch(childState, -beta, -alpha, maxDepth - 1);

      if (score >= beta) return beta;
      if (score > alpha) alpha = score;
    }

    return alpha;
  }

  // ===================== Evaluation =====================

  /**
   * Static evaluation of position (centipawns, from side-to-move perspective)
   */
  private evaluate(state: SearchState): number {
    const turn = state.engine.getTurn();
    const pieces = state.engine.getAllPieces();

    let score = 0;

    for (const piece of pieces) {
      const value = PIECE_VALUES[piece.type];
      const positional = this.getPositionalBonus(piece, state.boardCenter);
      const pieceScore = value + positional;

      if (piece.color === 'w') {
        score += pieceScore;
      } else {
        score -= pieceScore;
      }
    }

    // Mobility bonus (approximate: count legal moves for side to move)
    score += this.getMobilityBonus(state);

    // Check penalty for side to move
    if (state.engine.isCheck()) {
      score += turn === 'w' ? -30 : 30;
    }

    // Note: difficulty randomness is applied at root level via applyDifficultyRandomness(),
    // not during search, to keep alpha-beta pruning stable.

    // Return from perspective of side to move
    return turn === 'w' ? score : -score;
  }

  /**
   * Positional bonus for a piece based on its location
   */
  private getPositionalBonus(piece: Piece, boardCenter: { file: number; rank: number }): number {
    const file = piece.square.charCodeAt(0) - 97;
    const rank = parseInt(piece.square[1]) - 1;

    const centerDistFile = Math.abs(file - boardCenter.file);
    const centerDistRank = Math.abs(rank - boardCenter.rank);
    const centerDist = centerDistFile + centerDistRank;

    let bonus = 0;

    switch (piece.type) {
      case 'n':
        // Knights love the center
        bonus = Math.max(0, 4 - centerDist) * 8;
        break;
      case 'b':
        // Bishops benefit from centralization
        bonus = Math.max(0, 4 - centerDist) * 4;
        break;
      case 'p': {
        // Pawns get bonus for advancement
        const advancement = piece.color === 'w' ? rank : (7 - rank);
        bonus = advancement * 8;
        break;
      }
      case 'r': {
        // Rook on 7th rank bonus
        const seventhRank = piece.color === 'w' ? 6 : 1;
        if (rank === seventhRank) bonus = 20;
        break;
      }
      case 'k': {
        // King safety: prefer edges in middlegame
        const edgeBonus = Math.min(centerDistFile, centerDistRank);
        bonus = edgeBonus * 5;
        break;
      }
    }

    return bonus;
  }

  /**
   * Mobility bonus: +5 centipawns per legal move for side to move
   */
  private getMobilityBonus(state: SearchState): number {
    const turn = state.engine.getTurn();
    const pieces = state.engine.getAllPieces().filter(p => p.color === turn);
    let moveCount = 0;

    for (const piece of pieces) {
      const moves = this.getFilteredMoves(state, piece);
      moveCount += moves.length;
    }

    // Return from white's perspective (caller handles negation)
    return (turn === 'w' ? 1 : -1) * moveCount * 5;
  }


  // ===================== Move Generation =====================

  /**
   * Generate all legal moves for the current position, filtered by obstacles/walls
   */
  private generateMoves(state: SearchState): ScoredMove[] {
    const turn = state.engine.getTurn();
    const pieces = state.engine.getAllPieces().filter(p => p.color === turn);
    const allMoves: ScoredMove[] = [];

    for (const piece of pieces) {
      const targets = this.getFilteredMoves(state, piece);
      for (const to of targets) {
        const toRank = parseInt(to[1]);
        // Pawn promotion at the board's actual last rank for each color
        // (restricted boards promote at e.g. rank 7 / rank 2, not 8 / 1).
        if (
          piece.type === 'p' &&
          ((piece.color === 'w' && toRank === state.maxRank) ||
           (piece.color === 'b' && toRank === state.minRank))
        ) {
          allMoves.push({ from: piece.square, to, score: 0, promotion: 'q' });
        } else {
          allMoves.push({ from: piece.square, to, score: 0 });
        }
      }
    }

    return allMoves;
  }

  /**
   * Get legal moves for a piece, filtered by obstacles and walls
   * Mirrors ChessGame.getLegalMoves() filtering logic
   */
  private getFilteredMoves(state: SearchState, piece: Piece): Square[] {
    const engineMoves = state.engine.getLegalMoves(piece.square);

    return engineMoves.filter(to => {
      // Obstacles and water block all pieces
      if (state.obstacles.has(to) || state.waterSquares.has(to)) {
        return false;
      }
      // Check wall blocking (knights jump over walls)
      if (piece.type !== 'n' && this.isBlockedByWall(piece.square, to, piece.type, state)) {
        return false;
      }
      return true;
    });
  }

  /**
   * MVV-LVA move ordering: captures first (most valuable victim, least valuable attacker)
   */
  private orderMoves(moves: ScoredMove[], state: SearchState): void {
    for (const move of moves) {
      const target = state.engine.getPieceAt(move.to);
      const attacker = state.engine.getPieceAt(move.from);

      if (target && attacker) {
        move.score = PIECE_VALUES[target.type] * 10 - PIECE_VALUES[attacker.type];
      } else if (move.promotion) {
        move.score = PIECE_VALUES[move.promotion] * 10;
      } else {
        move.score = 0;
      }
    }
    moves.sort((a, b) => b.score - a.score);
  }

  /**
   * Clone engine, apply move, return new SearchState (or null if illegal)
   */
  private makeSearchMove(state: SearchState, move: ScoredMove): SearchState | null {
    const cloned = state.engine.clone();
    const result = cloned.makeMove(move.from, move.to, move.promotion);
    if (!result.isLegal) return null;

    return {
      engine: cloned,
      obstacles: state.obstacles,
      waterSquares: state.waterSquares,
      walls: state.walls,
      boardCenter: state.boardCenter,
      minRank: state.minRank,
      maxRank: state.maxRank,
    };
  }

  // ===================== Difficulty =====================

  /**
   * Apply difficulty-based move selection from the scored move list
   */
  private applyDifficultyRandomness(moves: ScoredMove[]): ScoredMove | null {
    if (moves.length === 0) return null;

    switch (this.aiConfig.difficulty) {
      case 'beginner':
        // Pick from all moves, weighted by score
        return this.weightedRandomPick(moves);
      case 'easy': {
        // Pick from top 5
        const top5 = moves.slice(0, Math.min(5, moves.length));
        return this.weightedRandomPick(top5);
      }
      case 'medium': {
        // Pick from top 3
        const top3 = moves.slice(0, Math.min(3, moves.length));
        return this.weightedRandomPick(top3);
      }
      case 'hard':
      case 'expert':
      default:
        return moves[0];
    }
  }

  /**
   * Weighted random pick: higher-scored moves are more likely to be chosen
   */
  private weightedRandomPick(moves: ScoredMove[]): ScoredMove {
    if (moves.length === 1) return moves[0];

    // Normalize so all weights are positive (min score -> weight 100)
    const minScore = Math.min(...moves.map(m => m.score));
    const weights = moves.map(m => m.score - minScore + 100);
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);

    let random = Math.random() * totalWeight;
    for (let i = 0; i < moves.length; i++) {
      random -= weights[i];
      if (random <= 0) return moves[i];
    }
    return moves[moves.length - 1];
  }

  // ===================== Board Utilities =====================

  private computeRankBounds(validSquares: Set<string>): { minRank: number; maxRank: number } {
    let minRank = 8, maxRank = 1;
    for (const sq of validSquares) {
      const r = parseInt(sq[1]);
      if (r < minRank) minRank = r;
      if (r > maxRank) maxRank = r;
    }
    return { minRank, maxRank };
  }

  private computeBoardCenter(validSquares: Set<string>): { file: number; rank: number } {
    let totalFile = 0;
    let totalRank = 0;
    let count = 0;

    for (const sq of validSquares) {
      totalFile += sq.charCodeAt(0) - 97;
      totalRank += parseInt(sq[1]) - 1;
      count++;
    }

    return {
      file: count > 0 ? totalFile / count : 3.5,
      rank: count > 0 ? totalRank / count : 3.5,
    };
  }

  // ===================== Wall/Obstacle Checking =====================
  // Mirrors ChessGame's logic — takes SearchState instead of using `this`

  private isBlockedByWall(
    from: Square,
    to: Square,
    _pieceType: PieceType,
    state: SearchState
  ): boolean {
    const fromFile = from.charCodeAt(0) - 97;
    const fromRank = parseInt(from[1]);
    const toFile = to.charCodeAt(0) - 97;
    const toRank = parseInt(to[1]);

    const fileDiff = toFile - fromFile;
    const rankDiff = toRank - fromRank;

    const isOrthogonal = fileDiff === 0 || rankDiff === 0;
    const isDiagonal = Math.abs(fileDiff) === Math.abs(rankDiff);

    if (isOrthogonal) {
      return this.checkOrthogonalPath(fromFile, fromRank, toFile, toRank, state);
    } else if (isDiagonal) {
      return this.checkDiagonalPath(fromFile, fromRank, toFile, toRank, state);
    }

    return false;
  }

  private checkOrthogonalPath(
    fromFile: number,
    fromRank: number,
    toFile: number,
    toRank: number,
    state: SearchState
  ): boolean {
    let direction: string;
    let stepFile = 0;
    let stepRank = 0;

    if (toRank > fromRank) { direction = 'north'; stepRank = 1; }
    else if (toRank < fromRank) { direction = 'south'; stepRank = -1; }
    else if (toFile > fromFile) { direction = 'east'; stepFile = 1; }
    else { direction = 'west'; stepFile = -1; }

    const opposite = this.getOppositeDirection(direction);

    let currentFile = fromFile;
    let currentRank = fromRank;

    while (currentFile !== toFile || currentRank !== toRank) {
      const currentSquare = String.fromCharCode(97 + currentFile) + currentRank;
      const currentWalls = state.walls.get(currentSquare);
      if (currentWalls && currentWalls.has(direction)) return true;

      currentFile += stepFile;
      currentRank += stepRank;

      const nextSquare = String.fromCharCode(97 + currentFile) + currentRank;
      const nextWalls = state.walls.get(nextSquare);
      if (nextWalls && nextWalls.has(opposite)) return true;
    }

    return false;
  }

  private checkDiagonalPath(
    fromFile: number,
    fromRank: number,
    toFile: number,
    toRank: number,
    state: SearchState
  ): boolean {
    const fileStep = toFile > fromFile ? 1 : -1;
    const rankStep = toRank > fromRank ? 1 : -1;

    let currentFile = fromFile;
    let currentRank = fromRank;

    while (currentFile !== toFile && currentRank !== toRank) {
      const nextFile = currentFile + fileStep;
      const nextRank = currentRank + rankStep;

      const adj1 = String.fromCharCode(97 + currentFile) + nextRank;
      const adj2 = String.fromCharCode(97 + nextFile) + currentRank;

      if (state.obstacles.has(adj1) && state.obstacles.has(adj2)) return true;

      currentFile += fileStep;
      currentRank += rankStep;
    }

    return false;
  }

  private getOppositeDirection(direction: string): string {
    switch (direction) {
      case 'north': return 'south';
      case 'south': return 'north';
      case 'east': return 'west';
      case 'west': return 'east';
      default: return '';
    }
  }
}
